import {
  Box,
  HStack,
  Skeleton,
  Switch,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import { useApprovalEnabled } from "../../../hooks/approvals/useApprovalEnabled";
import { useContextTypeSettingsOrganization } from "../dashboard-layout";
import { updateOrganization } from "../../../graphql/mutations";
import {
  UpdateOrganizationInput,
  UpdateOrganizationMutation,
  UserOrganizationRole,
} from "../../../API";
import { useUserOrganizations } from "../../../hooks/use-user-organizations";
import { useUsers } from "../../../hooks/use-users";
import { useOrgPendingApprovalCount } from "../../../hooks/approvals/useOrgPendingApprovalCount";
import { Select, Option as SelectOption } from "../../../components/select";
import { OrganizationFull } from "../../../models";

const GeneralSettings = () => {
  const { user, selectedOrganization, setSelectedOrganization, bookkeepings } =
    useContextTypeSettingsOrganization();
  const isApprovalEnabled = useApprovalEnabled(selectedOrganization?.id);
  const toast = useToast();

  // Users eligible as approvers: admin + head roles in this org (D-22)
  const { userOrganizations } = useUserOrganizations({ selectedOrganization });
  const { users } = useUsers({ userOrganizations });

  const approverUserIds = useMemo(
    () =>
      new Set(
        (userOrganizations ?? [])
          .filter(
            (uo) =>
              !uo.deletedAt &&
              (uo.role === UserOrganizationRole.admin ||
                uo.role === UserOrganizationRole.head)
          )
          .map((uo) => uo.userId)
      ),
    [userOrganizations]
  );

  const approverOptions: SelectOption[] = useMemo(() => {
    return (users ?? [])
      .filter((u) => !u.deletedAt && approverUserIds.has(u.id))
      .sort((a, b) =>
        (a.lastName ?? "").localeCompare(b.lastName ?? "", "de-CH")
      )
      .map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`,
      }));
  }, [users, approverUserIds]);

  // Pre-disable guard — D-15 / D-31. Approvals are ORG-WIDE (rules scope
  // across bookkeepings per RULE-03), so the guard sums pending counts
  // across ALL bookkeeping-years of ALL bookkeepings in the org via
  // useOrgPendingApprovalCount (TanStack useQueries parallelism).
  const { count: pendingApprovalCount, isLoading: countLoading } =
    useOrgPendingApprovalCount(bookkeepings);

  const refreshLocalOrg = (patch: Partial<OrganizationFull>) => {
    if (!selectedOrganization) return;
    setSelectedOrganization({ ...selectedOrganization, ...patch });
  };

  const handleToggle = async () => {
    if (!selectedOrganization || !user) return;
    const nextEnabled = !isApprovalEnabled;

    // D-15: block disable while pending approvals exist or counts still loading
    if (
      !nextEnabled &&
      (countLoading || !bookkeepings || pendingApprovalCount > 0)
    ) {
      toast({
        title:
          "Freigabeprozess kann nicht deaktiviert werden, solange ausstehende Freigaben existieren.",
        status: "warning",
        duration: 5000,
      });
      return;
    }

    // D-17 + WR-03 / MGMT-05 / D-22: auto-fill only when enabling AND the
    // toggling user is eligible (admin/head). Accountants can view settings
    // (showSettings allows them) but must not self-assign as employee default
    // approver — the dropdown itself is filtered to admin/head, so writing a
    // non-eligible id would make the assigned value not selectable in the UI.
    const hasAnyPerUserOverride = (userOrganizations ?? []).some(
      (uo) => !uo.deletedAt && Boolean(uo.approverUserId)
    );
    const togglerIsEligible = approverUserIds.has(user.id);
    const shouldAutoFill =
      nextEnabled &&
      togglerIsEligible &&
      !selectedOrganization.defaultEmployeeApproverUserId &&
      !hasAnyPerUserOverride;

    const input: UpdateOrganizationInput = shouldAutoFill
      ? {
          id: selectedOrganization.id,
          approvalEnabled: true,
          defaultEmployeeApproverUserId: user.id,
        }
      : { id: selectedOrganization.id, approvalEnabled: nextEnabled };

    try {
      await API.graphql<GraphQLQuery<UpdateOrganizationMutation>>(
        graphqlOperation(updateOrganization, { input })
      );
      refreshLocalOrg(
        shouldAutoFill
          ? {
              approvalEnabled: true,
              defaultEmployeeApproverUserId: user.id,
            }
          : { approvalEnabled: nextEnabled }
      );
      if (shouldAutoFill) {
        toast({
          title:
            "Du wurdest als Standardfreigeber gesetzt. Du kannst dies unten ändern.",
          status: "info",
          duration: 5000,
        });
      } else if (
        nextEnabled &&
        !togglerIsEligible &&
        !selectedOrganization.defaultEmployeeApproverUserId &&
        !hasAnyPerUserOverride
      ) {
        toast({
          title:
            "Freigabeprozess aktiviert. Bitte wähle unten einen Standardfreigeber aus.",
          status: "info",
          duration: 5000,
        });
      }
    } catch {
      toast({
        title: "Fehler beim Aktualisieren des Freigabeprozesses.",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDefaultApproverChange = async (nextUserId: string | null) => {
    if (!selectedOrganization) return;
    try {
      await API.graphql<GraphQLQuery<UpdateOrganizationMutation>>(
        graphqlOperation(updateOrganization, {
          input: {
            id: selectedOrganization.id,
            defaultEmployeeApproverUserId: nextUserId,
          },
        })
      );
      refreshLocalOrg({ defaultEmployeeApproverUserId: nextUserId });
      toast({
        title: "Standardfreigeber aktualisiert.",
        status: "success",
        duration: 3000,
      });
    } catch {
      toast({
        title: "Fehler beim Aktualisieren des Standardfreigebers.",
        status: "error",
        duration: 3000,
      });
    }
  };

  const dropdownDisabled =
    !isApprovalEnabled || !users || approverOptions.length === 0;

  const dropdownPlaceholder =
    users && approverOptions.length === 0
      ? 'Keine Freigeber verfügbar – fügen Sie zuerst Admins oder Heads im Tab "Teammitglieder" hinzu.'
      : "Standardfreigeber auswählen";

  return (
    <VStack width="full" alignItems="start" spacing="20px">
      <VStack alignItems="start" spacing="4px">
        <Text fontSize={18} fontWeight="semibold" color="gray.900">
          Allgemein
        </Text>
        <Text fontSize={14} color="gray.600">
          Allgemeine Einstellungen für Ihre Organisation
        </Text>
      </VStack>
      <Box
        width="full"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="8px"
        p="20px"
      >
        <VStack spacing="16px" alignItems="stretch">
          {/* Row 1: master switch */}
          <HStack justify="space-between" align="start">
            <VStack alignItems="start" spacing="4px" flex={1}>
              <Text fontSize={14} fontWeight="semibold" color="gray.900">
                Freigabeprozess
              </Text>
              <Text fontSize={14} color="gray.600">
                Aktivieren Sie den Freigabeprozess, um Ausgaben vor der
                Verbuchung genehmigen zu lassen.
              </Text>
            </VStack>
            <Switch
              colorScheme="primary"
              isChecked={isApprovalEnabled}
              onChange={handleToggle}
              aria-label="Freigabeprozess aktivieren"
              isDisabled={countLoading || !bookkeepings}
            />
          </HStack>

          {/* Row 2: Standardfreigeber dropdown (D-27) */}
          <VStack alignItems="stretch" spacing="4px">
            <Text fontSize={14} fontWeight="semibold" color="gray.900">
              Standardfreigeber für Mitarbeiter
            </Text>
            <Text fontSize={14} color="gray.600">
              Wird angewendet, wenn ein Mitarbeiter keinen eigenen Freigeber
              hat.
            </Text>
            {users === undefined ? (
              <Skeleton height="40px" />
            ) : (
              <Select
                value={
                  approverOptions.find(
                    (o) =>
                      o.value ===
                      selectedOrganization?.defaultEmployeeApproverUserId
                  ) ?? null
                }
                options={approverOptions}
                placeholder={dropdownPlaceholder}
                aria-label="Standardfreigeber für Mitarbeiter"
                onChange={(option) =>
                  handleDefaultApproverChange(
                    (option as SelectOption | null)?.value?.toString() ?? null
                  )
                }
                isClearable
                isDisabled={dropdownDisabled}
              />
            )}
            {!isApprovalEnabled && (
              <Text fontSize={14} color="gray.500">
                Aktivieren Sie zuerst den Freigabeprozess.
              </Text>
            )}
          </VStack>
        </VStack>
      </Box>
    </VStack>
  );
};

export default GeneralSettings;
